import {Component, forwardRef, inject, Input, OnInit} from "@angular/core";
import {MatFormFieldModule} from "@angular/material/form-field";
import {FlexLayoutModule} from "@angular/flex-layout";
import {
  FormBuilder,
  FormControl, FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import {AsyncPipe, JsonPipe} from "@angular/common";
import {MatInputModule} from "@angular/material/input";
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import {AbstractControlValueAccessor} from "../abstract-control-value-accessor";
import {HexColorValue} from "@portal-workspace/grow-shared-library";
import {Subscription} from "rxjs";
import {UntilDestroy} from "@ngneat/until-destroy";
import {
  createHexColorInputMask, createHexOrRgbColorInputMask,
  formControlErrorKeys, formControlErrorMessage,
  setupUntilDestroy,
  validHexColorValidator, validHexOrRgbColorValidator
} from "@portal-workspace/grow-ui-library";
import {delay, tap} from "rxjs/operators";
import {InputMaskModule, InputmaskOptions} from '@ngneat/input-mask';
import {DisableControlDirective} from '../../directives/disable-control.directive';


@UntilDestroy({arrayName: 'subscriptions'})
@Component({
  selector: 'hex-color',
  templateUrl: './hex-color.component.html',
  styleUrls: ['./hex-color.component.scss'],
  standalone: true,
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(()=>HexColorComponent), multi: true},
    {provide: MARK, useExisting: forwardRef(() => HexColorComponent)},
  ],
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FlexLayoutModule,
    FormsModule,
    ReactiveFormsModule,
    InputMaskModule,
    JsonPipe,
    AsyncPipe,
    DisableControlDirective,
  ],
})
export class HexColorComponent extends AbstractControlValueAccessor<HexColorValue> implements OnInit, Mark {

  @Input({required: false}) title: string = 'Color (in Hex)'
  @Input({required: false}) allowRgb = true;

  bgColor: string = '';

  createHexColorInputMask!: InputmaskOptions<unknown>;

  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;

  subscriptions: Subscription[] = [];

  formBuilder = inject(FormBuilder);
  formControl!: FormControl<HexColorValue>;
  formGroup!: FormGroup<{
    color: FormControl<HexColorValue>
  }>;

  ngOnInit() {
    setupUntilDestroy(this);

    this.formControl = this.formBuilder.control(
      null,
      this.allowRgb ? [Validators.required, validHexOrRgbColorValidator()] : [Validators.required, validHexColorValidator()],
    );
    this.formGroup = this.formBuilder.group({
      color: this.formControl,
    });

    this.createHexColorInputMask = this.allowRgb ? createHexOrRgbColorInputMask() : createHexColorInputMask();

    this.subscriptions.push(this.formControl.valueChanges.pipe(
      delay(10),
      tap(v => {
        if (this.formControl.valid) {
          this.bgColor = v ?? '';
          this.propagateChange(v);
        } else {
          this.bgColor = '';
          this.propagateChange(null);
        }
      }),
    ).subscribe());
  }

  mark(): void {
    this.formGroup.markAllAsTouched();
  }

  override doWriteValue(v?: HexColorValue | undefined): void | HexColorValue {
    this.formControl.setValue(v ?? null);
  }

}
